// src/HSCodeFinder.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Search, Info, Copy, AlertTriangle, Loader, Filter } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { trackPageView, trackHSCodeFinderUsedEvent, HSCodeFinderUsedEvent } from '../../utlis/useAnalytics';
import { PageName } from '../../configs/config';

interface HSCode {
  hscode: string;
  description: string;
}

const HSCodeFinder: React.FC = () => {
  useEffect(() => {
    trackPageView(PageName.HSCode);
  }, []);

  // State variables
  const [searchType, setSearchType] = useState<'code' | 'description'>('code');
  const [searchInput, setSearchInput] = useState<string>('');
  const [hsData, setHsData] = useState<HSCode[]>([]);
  const [searchResults, setSearchResults] = useState<HSCode[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);
  const [searchPerformed, setSearchPerformed] = useState<boolean>(false);
  const [debouncedInput, setDebouncedInput] = useState<string>('');
  const inputTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce search input changes
  useEffect(() => {
    if (inputTimeoutRef.current) clearTimeout(inputTimeoutRef.current);
    inputTimeoutRef.current = setTimeout(() => setDebouncedInput(searchInput), 300);
    return () => {
      if (inputTimeoutRef.current) clearTimeout(inputTimeoutRef.current);
    };
  }, [searchInput]);

  // Load HS Codes data from API
  useEffect(() => {
    const loadHSCodes = async () => {
      setLoading(true);
      try {
        const res = await fetch('https://json-api-vercel-theta.vercel.app/api/hs-codes.js');
        if (!res.ok) throw new Error(`HTTP error! status: ${res.status}`);
        const data = (await res.json()) as HSCode[];
        setHsData(data);
      } catch (err) {
        console.error('Error loading HS Codes:', err);
        setError('Failed to load HS Codes. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    loadHSCodes();
  }, []);

  // Filter data based on search
  useEffect(() => {
    if (!debouncedInput.trim()) {
      setSearchResults([]);
      setSearchPerformed(false);
      return;
    }
    setSearchPerformed(true);
    const norm = debouncedInput.toLowerCase().trim();
    setSearchResults(
      hsData.filter((code) =>
        searchType === 'code'
          ? code.hscode.toLowerCase().includes(norm)
          : code.description.toLowerCase().includes(norm)
      )
    );
  }, [debouncedInput, searchType, hsData]);

  // Handlers
  const handleSearch = (e: React.FormEvent) => e.preventDefault();

  const handleCopy = (text: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopySuccess(text);
        setTimeout(() => setCopySuccess(null), 2000);
      })
      .catch((err) => console.error('Failed to copy:', err));
  };

  const resetSearch = () => {
    setSearchInput('');
    setDebouncedInput('');
    setSearchResults([]);
    setSearchPerformed(false);
  };

  const formatHSCode = (code: string): string => {
    if (code.length <= 2) return code;
    if (code.length <= 4) return `${code.slice(0, 2)}.${code.slice(2)}`;
    if (code.length <= 6) return `${code.slice(0, 4)}.${code.slice(4)}`;
    return `${code.slice(0, 6)}.${code.slice(6)}`;
  };

  // Track when user toggles search type
  const onSelectSearchType = (type: 'code' | 'description') => {
    setSearchType(type);
    if (type === 'code') {
      trackHSCodeFinderUsedEvent(HSCodeFinderUsedEvent.SelectedHSCode, {
        hscode: '',
        description: '',
      });
    } else {
      trackHSCodeFinderUsedEvent(HSCodeFinderUsedEvent.SelectedDescription, {
        hscode: '',
        description: '',
      });
    }
    resetSearch();
  };

  // Track when user selects a specific HS code row
  const onSelectHSCodeRow = (code: HSCode) => {
    trackHSCodeFinderUsedEvent(HSCodeFinderUsedEvent.SelectedHSCode, {
      hscode: code.hscode,
      description: code.description,
    });
  };

  return (
    <>
      <Helmet>
        <title>HS Code Finder – Search Harmonized System Codes Online | EximPe</title>
        <meta
          name="description"
          content="Use our free HS Code lookup tool to find the correct Harmonized System codes for your imports & exports. Verify customs tariffs and streamline international trade."
        />
      </Helmet>

      <div className="pt-24 pb-10 bg-gradient-to-b mt-10 from-white to-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-800 mb-6">
                HS Code Finder
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-2xl mx-auto">
                Find 8-digit Indian HS codes quickly for international trade classification, duties, and customs documentation.
              </p>
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 sm:p-8">
              {/* Search Type Toggle */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Search By
                </label>
                <div className="flex bg-gray-100 rounded-lg p-1 w-fit">
                  <button
                    onClick={() => onSelectSearchType('code')}
                    className={`px-4 py-2 rounded-md hover:cursor-pointer text-sm font-medium transition-all duration-200 ${
                      searchType === 'code'
                        ? 'bg-white text-[#1c9e86] shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    HS Code
                  </button>
                  <button
                    onClick={() => onSelectSearchType('description')}
                    className={`px-4 py-2 rounded-md hover:cursor-pointer text-sm font-medium transition-all duration-200 ${
                      searchType === 'description'
                        ? 'bg-white text-[#1c9e86] shadow-sm'
                        : 'text-gray-600 hover:text-gray-800'
                    }`}
                  >
                    Description
                  </button>
                </div>
              </div>

              {/* Search Bar */}
              <form onSubmit={handleSearch}>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {searchType === 'code' ? 'Enter HS Code' : 'Enter Product Description'}
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Search size={18} className="text-gray-400" />
                    </div>
                    <input
                      type="text"
                      className="pl-10 block w-full rounded-lg border border-gray-300 shadow-sm focus:ring-[#1c9e86] focus:border-[#1c9e86] p-3"
                      placeholder={`Search by ${
                        searchType === 'code' ? 'HS code' : 'product description'
                      }...`}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                    {searchInput && (
                      <button
                        type="button"
                        onClick={resetSearch}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 
                               1.414L8.586 10l-1.293 1.293a1 1 0 101.414 
                               1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 
                               10l1.293-1.293a1 1 0 00-1.414-1.414L10 
                               8.586 8.707 7.293z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              </form>

              {/* Loading Spinner */}
              {loading && (
                <div className="text-center p-12">
                  <Loader
                    size={36}
                    className="animate-spin mx-auto mb-4 text-[#1c9e86]"
                  />
                  <p className="text-gray-600">Loading HS codes...</p>
                </div>
              )}

              {/* Error Message */}
              {error && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 mb-4">
                  <div className="flex items-start">
                    <AlertTriangle
                      size={20}
                      className="mr-2 flex-shrink-0 mt-0.5"
                    />
                    <div>
                      <h3 className="text-sm font-medium text-amber-800">Error</h3>
                      <div className="mt-1 text-sm text-amber-700">{error}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Search Results */}
              {!loading && !error && searchPerformed && (
                <>
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center text-sm text-gray-600">
                      <Filter size={16} className="mr-1" />
                      <span>
                        {searchResults.length} results{' '}
                        {debouncedInput && (
                          <span>
                            {' '}
                            for <span className="font-medium">{debouncedInput}</span>
                          </span>
                        )}
                      </span>
                    </div>
                    {searchInput && (
                      <button
                        onClick={resetSearch}
                        className="text-[#1c9e86] text-sm hover:underline flex items-center"
                      >
                        Clear search
                      </button>
                    )}
                  </div>

                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <div className="p-4 bg-gray-50 border-b border-gray-200">
                      <h3 className="font-medium text-gray-800">Search Results</h3>
                    </div>
                    <div className="max-h-[600px] overflow-y-auto">
                      {searchResults.length > 0 ? (
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0 z-10">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                HS Code
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Description
                              </th>
                              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Copy
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {searchResults.slice(0, 100).map((code) => (
                              <tr
                                key={code.hscode}
                                className="hover:bg-gray-50 transition-colors cursor-pointer"
                                onClick={() => onSelectHSCodeRow(code)}
                              >
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className="font-mono text-gray-900">
                                    {formatHSCode(code.hscode)}
                                  </span>
                                </td>
                                <td className="px-6 py-4">
                                  <span className="text-gray-900">
                                    {code.description}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleCopy(code.hscode);
                                    }}
                                    className="text-gray-400 hover:text-[#1c9e86] focus:outline-none"
                                    title="Copy HS code"
                                  >
                                    {copySuccess === code.hscode ? (
                                      <span className="text-xs bg-green-100 text-green-800 px-1.5 py-0.5 rounded">
                                        Copied!
                                      </span>
                                    ) : (
                                      <Copy size={16} />
                                    )}
                                  </button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      ) : (
                        <div className="text-center py-12">
                          <div className="text-gray-400 mb-4">
                            <Search className="h-12 w-12 mx-auto" />
                          </div>
                          <h3 className="text-lg font-semibold text-gray-700 mb-2">
                            No matching HS codes found
                          </h3>
                          <p className="text-gray-500 mb-4">
                            Try adjusting your search criteria or try a different search term
                          </p>
                        </div>
                      )}
                      {searchResults.length > 100 && (
                        <div className="p-4 text-center text-gray-500 bg-gray-50 border-t border-gray-200">
                          Showing 100 of {searchResults.length} results. Refine your search
                          to see more specific matches.
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Initial Prompt (before search) */}
              {!loading && !error && !searchPerformed && (
                <div className="text-center py-12 border border-gray-200 rounded-lg">
                  <div className="text-gray-400 mb-4">
                    <Search className="h-12 w-12 mx-auto" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">
                    Enter search terms to find HS codes
                  </h3>
                  <p className="text-gray-500 mb-4">
                    Type in a product description or HS code to see matching results
                  </p>
                </div>
              )}

              {/* Info Box */}
              <div className="mt-8 p-4 bg-blue-50 rounded-lg text-sm text-blue-800 border border-blue-100">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <Info className="h-5 w-5 text-blue-500" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <strong>What are HS Codes?</strong> Harmonized System (HS) codes are a
                      standardized numerical method of classifying traded products. It is
                      used by customs authorities around the world to identify products when
                      assessing duties and taxes and for gathering statistics.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* SEO Content Section */}
            <div className="mt-16 bg-white rounded-xl shadow-lg p-6 sm:p-8 prose max-w-none text-gray-700">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-800 mb-6 text-center lg:text-left md:text-left">
                What is an HS Code?
              </h2>

              <p>
                Harmonized System (HS) codes are worldwide numerical systems used to organize
                products at customs and in international trade. In India, these are extended to
                8-digit ITC-HS codes by the Directorate General of Foreign Trade (DGFT). The
                World Customs Organization (WCO) controls the base 6-digit structure, and over
                200 countries rely on it to cover more than 98% of traded goods.
              </p>

              <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4 text-center lg:text-left md:text-left">
                Why is the HS Code Important?
              </h3>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>
                  Import and export shipments must have a classification for goods so that
                  customs clearance is smooth.
                </li>
                <li>
                  It determines taxes, customs duties, and eligibility for trade agreements or
                  exemptions.
                </li>
                <li>
                  You need this information to prepare commercial invoices, certificates of
                  origin, and other customs documents correctly.
                </li>
                <li>
                  Using the correct 8-digit ITC-HS code helps avoid delays at Indian customs
                  and ensures the right duties are applied.
                </li>
              </ul>

              <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4 text-center lg:text-left md:text-left">
                Structure of the Indian HS Code (ITC-HS)
              </h3>
              <p>
                The core HS code is a 6-digit number. India then appends two additional digits
                (to make 8) for more granular classification. The breakdown is:
              </p>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>
                  <strong>First 2 digits:</strong> Chapter (product category)
                </li>
                <li>
                  <strong>Next 2 digits:</strong> Heading (subcategory)
                </li>
                <li>
                  <strong>Next 2 digits:</strong> Subheading (specific product)
                </li>
                <li>
                  <strong>Last 2 digits:</strong> Indian ITC extension (further national subdivision)
                </li>
              </ul>

              <h4 className="text-lg font-bold text-gray-800 mt-6 mb-3">Example:</h4>
              <p className="font-mono bg-gray-50 p-3 rounded-lg">
                HS Code 1006.30 – Semi-milled or wholly milled rice<br />
                1006.30 00 33 – India’s 8-digit ITC-HS code for “Semi-milled rice, premilled,
                  polished, glazed, or otherwise, of a variety known as ‘Basmati’”
              </p>

              <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4 text-center lg:text-left md:text-left">
                When Do You Need an 8-Digit HS Code?
              </h3>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>
                  Every international shipment into or out of India requires the correct
                  8-digit ITC-HS code.
                </li>
                <li>
                  Use it when preparing customs declarations, import licences, and shipping
                  documents.
                </li>
                <li>
                  To calculate applicable duties, check eligibility for Free Trade Agreements,
                  and comply with Indian customs regulations.
                </li>
                <li>
                  Many exporters/importers also reference the 8-digit code in GST filings and
                  statistical reports.
                </li>
              </ul>

              <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4 text-center lg:text-left md:text-left">
                How to Find the Right 8-Digit ITC-HS Code
              </h3>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>
                  Access India’s official ITC-HS tariff schedule (PDF or online) from the DGFT
                  or Customs website.
                </li>
                <li>
                  Use this HS Code Finder tool to search by 8-digit code prefix or product
                  description.
                </li>
                <li>
                  Consult a customs broker or logistics partner if you’re unsure about the
                  exact 8-digit subdivision.
                </li>
                <li>
                  Remember that the last two digits in India often specify packaging size,
                  processing state, or specific varietal characteristics.
                </li>
              </ul>

              <h3 className="text-xl font-bold text-gray-800 mt-8 mb-4 text-center lg:text-left md:text-left">
                Key Points to Remember
              </h3>
              <ul className="list-disc pl-6 mt-4 space-y-2">
                <li>
                  The first 6 digits are universal (WCO level), but India requires 8 digits for
                  all tariff classifications.
                </li>
                <li>
                  Always verify the full 8-digit ITC-HS code before filing customs paperwork in
                  India.
                </li>
                <li>
                  An incorrect code can lead to wrong duty calculations, shipment delays, or
                  even confiscation.
                </li>
                <li>
                  For any clarifications, refer to the DGFT’s official ITC-HS documentation or
                  consult a licensed customs house agent.
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HSCodeFinder;
